import { c as _c } from "react/compiler-runtime";
import { IcoAsterix16, IcoCircle16 } from '@onefootprint/icons';
import { Tooltip } from '@onefootprint/ui';
import * as RadixToggleGroup from '@radix-ui/react-toggle-group';
import { cx } from 'class-variance-authority';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
export type ToggleOption = 'required' | 'optional';
export type ToggleSize = 'default' | 'compact';
type RequiredToggleProps = {
  id: string;
  value: ToggleOption;
  disabled?: boolean;
  size?: ToggleSize;
  onOptionChange: (value: ToggleOption) => void;
};

// `compact` sizes the item so the control's total height matches the inline chip
// (CHIP_CLASS_NAME) it sits beside in a dense row.
const SIZES: Record<ToggleSize, {
  root?: string;
  item: string;
  icon?: string;
}> = {
  default: {
    root: 'min-h-8',
    item: 'size-6.5'
  },
  compact: {
    item: 'size-4',
    icon: 'size-3.5'
  }
};
const RequiredToggle = t0 => {
  const $ = _c(33);
  const {
    onOptionChange,
    value,
    id,
    disabled,
    size: t1
  } = t0;
  const size = t1 === undefined ? "default" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "components.requiredToggle"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("common", t2);
  let t3;
  if ($[1] !== t) {
    t3 = t(_temp);
    $[1] = t;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t) {
    t4 = t(_temp2);
    $[3] = t;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = {
      value: "required",
      label: t3,
      hint: t4,
      icon: IcoAsterix16
    };
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t) {
    t6 = t(_temp3);
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t) {
    t7 = t(_temp4);
    $[10] = t;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = {
      value: "optional",
      label: t6,
      hint: t7,
      icon: IcoCircle16
    };
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t5 || $[16] !== t8) {
    t9 = [t5, t8];
    $[15] = t5;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const options = t9;
  const sizing = SIZES[size];
  let t10;
  if ($[18] !== sizing.root) {
    t10 = cx("flex gap-0.5 p-0.5 border border-tertiary w-fit rounded bg-primary isolate content-box", sizing.root);
    $[18] = sizing.root;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== disabled || $[21] !== id || $[22] !== options || $[23] !== sizing.icon || $[24] !== sizing.item || $[25] !== value) {
    t11 = <AnimatePresence>{options.map(t12 => {
        const {
          value: toggleValue,
          icon: Icon,
          label,
          hint
        } = t12;
        const isActive = value === toggleValue;
        return <Tooltip content={hint} delayOpen={300} disabled={disabled} key={toggleValue}><motion.div className="relative" layoutRoot={true}><RadixToggleGroup.Item value={toggleValue} aria-label={label} className={cx("justify-center rounded-sm flex items-center cursor-pointer z-10", sizing.item, {
              "hover:bg-hover-control transition-colors duration-200 ease": !isActive && !disabled
            })}><Icon color={isActive ? "primary" : "tertiary"} className={sizing.icon} /></RadixToggleGroup.Item>{isActive && <motion.div layoutId={id} className="absolute inset-0 rounded-sm bg-senary -z-1" initial={false} transition={{
              duration: 0.1,
              ease: [0.25, 0.46, 0.45, 0.94]
            }} />}</motion.div></Tooltip>;
      })}</AnimatePresence>;
    $[20] = disabled;
    $[21] = id;
    $[22] = options;
    $[23] = sizing.icon;
    $[24] = sizing.item;
    $[25] = value;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== disabled || $[28] !== onOptionChange || $[29] !== t10 || $[30] !== t11 || $[31] !== value) {
    t12 = <RadixToggleGroup.Root type="single" className={t10} value={value} disabled={disabled} onValueChange={onOptionChange}>{t11}</RadixToggleGroup.Root>;
    $[27] = disabled;
    $[28] = onOptionChange;
    $[29] = t10;
    $[30] = t11;
    $[31] = value;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  return t12;
};
export default RequiredToggle;
function _temp($) {
  return $.required;
}
function _temp2($_0) {
  return $_0.requiredHint;
}
function _temp3($_1) {
  return $_1.optional;
}
function _temp4($_2) {
  return $_2.optionalHint;
}